import { c as _c } from "react/compiler-runtime";
import { Account } from '@commutifi-fe/interfaces';
import { type AccountVehicleBookingProps } from '@commutifi/models/AccountVehiclesBookings';
import { BookingProps } from '@commutifi/models/Bookings';
import { InventoryProps } from '@commutifi/models/Inventory';
import { InventoryDetailsProps } from '@commutifi/models/InventoryDetails';
import { PasseProps } from '@commutifi/models/Passes';
import { TransactionProps } from '@commutifi/models/Transaction';
import { useMutation } from 'api/reactQuery';
import { invalidateBookingLimits } from 'api/modules/accounts/bookingLimitsCache';
import { Paths } from 'shared/interfaces';
import { RequestParams } from 'shared/interfaces/Api';
import { Overwrite, Primitive } from 'utility-types';
import { apiCall, dashboardApiServer } from '../../index';
import { DetailedPlan } from '../commuterPlans/types';
import { InvoiceWithRelations } from '../invoices';
import endpoints from './endpoints';
export type { BookingProps } from '@commutifi/models/Bookings';
export type { AccountVehicleBookingProps as AccountVehiclesBookings } from '@commutifi/models/AccountVehiclesBookings';
const apiDashboardServer = apiCall(dashboardApiServer);
export type BookingInventoryItem = Required<Pick<InventoryProps, 'id' | 'serviceId'>> & Partial<Omit<InventoryProps, 'id' | 'serviceId'>> & {
  inventoryDetails?: InventoryDetailsProps;
};
export type DetailedBooking = Overwrite<BookingProps, {
  plan?: DetailedPlan;
  account?: Partial<Account>;
  inventory?: BookingInventoryItem;
}> & {
  canOwnerEdit?: boolean;
  /** YYYY-MM-DD blackout dates within this booking's maintenance window (from BE) */
  blackoutDates?: string[];
  maintenanceEndDate?: string | null;
  /** BE-computed overlap info for bookings sharing the same inventory item */
  inventoryConflicts?: {
    originalDateOverlaps: BookingConflictRef[];
    maintenanceDateOverlaps: BookingConflictRef[];
  };
};
export type BookingConflictRef = {
  id: string;
  accountName: string;
  accountEmail?: string;
};
export { BookingInclude } from '@commutifi/constants/Bookings';
export function fetchOneBooking<T = DetailedBooking>(bookingId: string, queryParams: RequestParams): Promise<T> {
  return apiDashboardServer(endpoints.GET.Booking.route(bookingId), {
    method: 'get',
    queryParams
  });
}
export type BookingInvoicesParams = RequestParams & {
  accountId?: string;
  generateNextPaymentBreakdown?: boolean;
} & { [K in Paths<Overwrite<InvoiceWithRelations, {
  transactions?: TransactionProps;
}>>]?: Primitive };
export type BookingInvoicesResponse = {
  records: InvoiceWithRelations[];
  nextInvoice: InvoiceWithRelations;
  nextInvoiceGenerationDate: string;
  generationDateOffset: number;
  _metadata: {
    totalCount: number;
    limit: number;
    offset: number;
  };
};
export const fetchBookingInvoices = (bookingId: string, queryParams: BookingInvoicesParams): Promise<BookingInvoicesResponse> => apiDashboardServer(endpoints.GET.BookingInvoices.route(bookingId), {
  method: 'get',
  queryParams
});
export const getAccountPasses = (bookingId: string, queryParams: RequestParams): Promise<PasseProps[]> => apiDashboardServer(endpoints.GET.AccountPasses.route(bookingId), {
  method: 'get',
  queryParams
});
export const patchBooking = (bookingId: string, data: Partial<DetailedBooking>, queryParams?: {
  activationEmail?: boolean;
  isExtendingBooking?: boolean;
}): Promise<BookingProps> => apiDashboardServer(endpoints.PATCH.Booking.route(bookingId), {
  method: 'patch',
  queryParams,
  data
});
export const batchUpdateBookings = (ids: string[], patch: Partial<BookingProps>) => apiDashboardServer(endpoints.PATCH.ManyBookings.route(), {
  method: 'patch',
  data: {
    ids,
    data: patch
  }
});
export const putBookingVehicles = (bookingId: string, data: Pick<AccountVehicleBookingProps, 'accountVehicleId'>[]): Promise<AccountVehicleBookingProps[]> => apiDashboardServer(endpoints.PUT.BookingAccountVehicles.route(bookingId), {
  method: 'put',
  data
});
export interface TerminateBookingResponse {
  activeUntil: string;
  chargedUntil: string;
}
export const postTerminateBooking = (bookingId: string, data: {
  endDate: string;
}): Promise<TerminateBookingResponse> => apiDashboardServer(endpoints.POST.TerminateBooking.route(bookingId), {
  method: 'post',
  data
});
export const batchTerminateBookings = (bookingIds: string[]) => apiDashboardServer(endpoints.POST.BatchTerminateBookings.route(), {
  method: 'post',
  data: {
    bookingIds
  }
});
export const batchConfirmTerminations = (bookingIds: string[]) => apiDashboardServer(endpoints.POST.BatchConfirmTermination.route(), {
  method: 'post',
  data: {
    bookingIds
  }
});
export const fetchFilledForm = (bookingId: string): Promise<{
  pdfDataUri: string;
}> => apiDashboardServer(endpoints.POST.FillPdf.route(bookingId), {
  method: 'post'
});
export const putAccountPasses = (bookingId: string, data: Omit<PasseProps, 'bookingId' | 'booking'>[]): Promise<PasseProps[]> => apiDashboardServer(endpoints.PUT.AccountPasses.route(bookingId), {
  method: 'put',
  data
});
export type PostBookingBody = Pick<BookingProps, 'accountId' | 'planId' | 'quantity'> & {
  startDate?: string;
};
export const postBooking = (data: PostBookingBody): Promise<BookingProps & {
  pdfDataUri?: string;
}> => apiDashboardServer(endpoints.POST.Bookings.route(), {
  method: 'post',
  data
});
export type PostAdminBookingBody = Pick<BookingProps, 'planId' | 'accountId' | 'startDate' | 'endDate' | 'bookedItem' | 'accessories' | 'phoneNumber' | 'hasEvCharging' | 'note'> & {
  inventoryDetailId?: string;
  quantity: number;
  enterpriseId: string;
  accountVehicleIds?: string[];
  passes?: Array<{
    type: string;
    passId: string;
  }>;
};
export const useConfirmBookingTermination = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useCancelBooking = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp2,
      onSettled: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useRejectBooking = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export type RejectBatchBookingsParams = {
  bookingIds: string[];
};
export const useRejectBatchBookings = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp5
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const postAdminBooking = (data: PostAdminBookingBody): Promise<BookingProps> => {
  const {
    enterpriseId,
    ...body
  } = data;
  return apiDashboardServer(endpoints.POST.AdminBookings.route(), {
    method: 'post',
    data: body,
    queryParams: {
      enterpriseId
    }
  });
};
async function _temp(bookingId) {
  return apiDashboardServer(endpoints.PATCH.BookingTerminationConfirmation.route(bookingId), {
    method: "patch"
  });
}
async function _temp2(bookingId) {
  return apiDashboardServer(endpoints.PATCH.CancelBooking.route(bookingId), {
    method: "patch"
  });
}
function _temp3() {
  return invalidateBookingLimits();
}
async function _temp4(bookingId) {
  return apiDashboardServer(endpoints.PATCH.RejectBooking.route(bookingId), {
    method: "patch"
  });
}
async function _temp5(params) {
  const response = await apiDashboardServer(endpoints.POST.BatchRejectBookings.route(), {
    method: "post",
    data: params
  });
  return response.data;
}